import React from 'react';
import { coreRoutes } from './core.routes';
import { itRoutes } from './it.routes';
import { adminRoutes } from './admin.routes';
import { hseRoutes } from './hse.routes';
import { qaqcRoutes } from './qaqc.routes';
import { materialRoutes } from './material.routes';
import { toRoutes } from './to.routes';
import { constructionRoutes } from './construction.routes';
import { machineryRoutes } from './machinery.routes';
import { dccRoutes } from './dcc.routes';
import { designRoutes } from './design.routes';
import { officialRoutes } from './official.routes';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../components/common/security/AuthContext';
import { canActOnPage, isPathAllowedForRole } from '../utils/permissions';
import i18n from '../i18n/i18n';
import InitialMenu from '../pages/InitialMenu';
import Login from '../pages/Login';
import FormPreview from '../components/common/Forms/FormPreview';
import UserProfile from '../pages/it/users/UserProfile';
import CorTasksPage from '../pages/core/CorTasksPage';
import { Result } from 'antd';
import type { MenuProps } from 'antd';
import { HomeOutlined, UserOutlined } from '@ant-design/icons';
import { MdPlumbing } from 'react-icons/md';
import { FaTools } from 'react-icons/fa';
import { TbDatabaseEdit, TbTruckDelivery, TbReportAnalytics } from 'react-icons/tb';
import { GiForklift, GiTeePipe } from 'react-icons/gi';

// const Placeholder = ({ title }: { title: string }) => <div style={{padding:32}}>{title} (Yapım Aşamasında)</div>;

// Yapım aşamasında sayfası için bileşen
const UnderConstruction = ({ title }: { title: string }) => (
  <Result
    status="info"
    title={`${title} Module`}
    subTitle="Under Construction."
  />
);

// Wrapper for react-icons to ensure valid JSX element typing
const RPlumbing: React.FC<any> = (props) =>
  React.createElement(MdPlumbing as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RFaTools: React.FC<any> = (props) =>
  React.createElement(FaTools as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RTbDatabaseEdit: React.FC<any> = (props) =>
  React.createElement(TbDatabaseEdit as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RTbTruckDelivery: React.FC<any> = (props) =>
  React.createElement(TbTruckDelivery as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RTbReportAnalytics: React.FC<any> = (props) =>
  React.createElement(TbReportAnalytics as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RGiForklift: React.FC<any> = (props) =>
  React.createElement(GiForklift as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RGiTeePipe: React.FC<any> = (props) =>
  React.createElement(GiTeePipe as unknown as React.ComponentType<any>, props) as React.ReactElement;
// Define types for our routes
export interface RouteItem {
  path: string;
  element: React.ReactNode;
  label?: string;
  /** i18n key (e.g. routes.core.folderUploads). When set, menu/search use translated label. */
  labelKey?: string;
  icon?: React.ReactNode;
  showInMenu?: boolean;
  /**
   * When true, this item stays a single header entry that navigates to `path`.
   * Descendants still render on the third menu row; they are not a dropdown on this item.
   */
  hideMenuChildren?: boolean;
  children?: Record<string, RouteItem>;
  // Optional: grandchildren are ONLY for menu grouping (dropdown under this item)
  // They are not used by router flattening; use normal children for routable items.
  grandchildren?: Record<string, RouteItem>;
}

/** Resolved menu/search label: `labelKey` via i18n, else static `label`. */
export function resolveRouteLabel(node: Pick<RouteItem, 'label' | 'labelKey'>): string {
  if (node.labelKey) {
    return String(i18n.t(node.labelKey));
  }
  return node.label ?? '';
}

export interface RouteConfig {
  path: string;
  element: React.ReactNode;
}

// Define route structure with metadata
export const routes: Record<string, RouteItem> = {
  root: {path: '/', element: <Navigate to="/home" replace />, showInMenu: false},
  login: {path: '/login', element: <Login />, label: 'Login', icon: <UserOutlined />, showInMenu: false},
  home: {path: '/home', element: <InitialMenu />, label: 'HOME', icon: <HomeOutlined />, showInMenu: false},

  formPreview: { path: '/form-preview', element: <FormPreview />, showInMenu: false },

  // Modular route imports (Core, IT, Admin, HSE modules extracted to separate files)
  ...coreRoutes,
  ...itRoutes,
  ...adminRoutes,
  ...hseRoutes,
  // Phase 1: externalized QA/QC routes
  ...qaqcRoutes,
  // Phase 2: externalized Material routes
  ...materialRoutes,
  // Phase 3: externalized TO routes
  ...toRoutes,
  // Phase 4: externalized Construction routes
  ...constructionRoutes,
  // Phase 5: externalized Machinery routes
  ...machineryRoutes,
  // Phase 6: externalized DCC routes
  ...dccRoutes,
  // Phase 7: externalized Design routes
  ...designRoutes,
  // Phase 8: externalized Official routes
  ...officialRoutes,


  profile: {path: '/profile', element: <UserProfile />, label: 'Profile', icon: <UserOutlined />, showInMenu: false },
  tasks: {path: '/tasks', element: <CorTasksPage />, label: 'My Tasks', showInMenu: false },

  notFound: {path: '*', element: (<Result status="404" title="404" subTitle="Page Not Found." />), showInMenu: false},
};

// Helper function to flatten routes for React Router
export const getFlattenedRoutes = (): RouteConfig[] => {
  const flattenRoutes = (routeObj: Record<string, RouteItem>, result: RouteConfig[] = []) => {
    Object.values(routeObj).forEach(route => {
      if (route.path && route.element) {
        result.push({
          path: route.path,
          element: route.element
        });
      }
      if (route.children) {
        flattenRoutes(route.children, result);
      }
      // Also include grandchildren so dropdown-only items still have real routes
      if (route.grandchildren) {
        flattenRoutes(route.grandchildren, result);
      }
    });
    return result;
  };
  return flattenRoutes(routes);
};

export interface RouteSearchItem {
  path: string;
  label: string;
  sectionLabel?: string;
  icon?: React.ReactNode;
}

/**
 * Build a search-friendly index of all navigable routes.
 * Intended for global navigation UIs (e.g., Ctrl+K command palette).
 */
export const getRouteSearchIndex = (): RouteSearchItem[] => {
  const items: RouteSearchItem[] = [];
  const excludedPaths = new Set<string>(['/', '*', '/login']);
  const seen = new Set<string>();

  const pushItem = (node: RouteItem) => {
    const path = node?.path;
    if (!path || excludedPaths.has(path)) return;
    if (!node.element) return;
    // Unlabeled hidden routes are redirects, not destinations.
    if (!node.label && !node.labelKey && node.showInMenu === false) return;
    if (seen.has(path)) return;
    seen.add(path);

    const sectionKey = getPathSection(path);
    const sectionLabel = routes[sectionKey]?.label;
    const label = (node.label || node.labelKey ? resolveRouteLabel(node) : path) as string;

    items.push({
      path,
      label,
      sectionLabel: sectionLabel ? String(sectionLabel) : undefined,
      icon: node.icon,
    });
  };

  const walk = (node?: RouteItem) => {
    if (!node) return;
    pushItem(node);
    if (node.children) {
      Object.values(node.children).forEach(walk);
    }
    if (node.grandchildren) {
      Object.values(node.grandchildren).forEach(walk);
    }
  };

  Object.values(routes).forEach(walk);

  // Preserve declaration order (no alphabetical sorting) so search follows menu ordering
  return items;
};

// Type for menu items (legacy internal). Prefer MenuProps['items'] for AntD Menu.
export interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  children?: MenuItem[];
}

export const getPathSection = (path: string): string => {
  // Root path is home section
  if (path === '/' || path === '/home') return 'home'; // Handle both '/' and '/home'

  // Find the top-level section that matches the path
  let bestMatch = 'home';
  let longestMatchLength = 0;

  for (const [key, route] of Object.entries(routes)) {
    // Check if the path starts with the route's base path
    // Ensure the route's path is not just '/'
    // Check if this match is longer than the previous best match
    if (route.path !== '/' && path.startsWith(route.path) && route.path.length > longestMatchLength) {
        bestMatch = key;
        longestMatchLength = route.path.length;
    }
  }
  return bestMatch;
};

// Custom hook to generate menu items specifically for the AppHeader
export const useMenuItems = () => {
  useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const { page_permissions, permitted_pages, role } = useAuth();

  // Read izni olmayan veya admin-only olup rolü yetmeyen sayfalar menüde görünmez
  const canReadPath = (path: string) =>
    isPathAllowedForRole(path, role) &&
    canActOnPage(page_permissions, permitted_pages, path, 'read');

  // Determine the current section name for the header title
  const currentSectionKey = getPathSection(currentPath);
  const currentSectionLabel = routes[currentSectionKey]?.label || ''; // Get label for title

  // Helper: find the deepest child whose path is a prefix of currentPath
  const getDeepestMatchingNode = (base: RouteItem, path: string): RouteItem => {
    let node: RouteItem = base;
    while (node.children) {
      let best: RouteItem | null = null;
      let longest = 0;
      for (const key in node.children) {
        const child = node.children[key];
        if (child?.path && path.startsWith(child.path) && child.path.length > longest) {
          best = child;
          longest = child.path.length;
        }
      }
      if (best && best.path !== node.path) {
        node = best;
      } else {
        break;
      }
    }
    return node;
  };

  // Helper: does node have at least one visible child or grandchild to render?
  const hasRenderableChildren = (node?: RouteItem): boolean => {
    if (!node) return false;
    const candidates = [
      ...Object.values(node.children ?? {}),
      ...Object.values(node.grandchildren ?? {}),
    ];
    for (const c of candidates) {
      if (!c) continue;
      if (c.path && c.showInMenu && (c.label || c.labelKey) && canReadPath(c.path)) return true;
      if (hasRenderableChildren(c)) return true;
    }
    return false;
  };

  // Helper: visible immediate descendants (either in `children` or `grandchildren`)
  // Note: some parts of the app store 3rd-level items under `children` (not `grandchildren`).
  const visibleDescendants = (node?: RouteItem): RouteItem[] => {
    const all = [
      ...Object.values(node?.children ?? {}),
      ...Object.values(node?.grandchildren ?? {}),
    ];
    return all.filter(
      (c): c is RouteItem =>
        Boolean(c && c.path && c.showInMenu && (c.label || c.labelKey)) &&
        (canReadPath(c!.path) || hasRenderableChildren(c))
    );
  };

  // Map a RouteItem into antd Menu item recursively (supports children + grandchildren at any depth)
  const toMenuItem = (node: RouteItem): NonNullable<MenuProps['items']>[number] => {
    const kids = node.hideMenuChildren ? [] : visibleDescendants(node);
    if (kids.length > 0) {
      return {
        key: node.path,
        icon: node.icon as React.ReactNode,
        label: resolveRouteLabel(node),
        children: kids.map(k => toMenuItem(k)),
      } as NonNullable<MenuProps['items']>[number];
    }

    return {
      key: node.path,
      icon: node.icon as React.ReactNode,
      label: resolveRouteLabel(node),
    } as NonNullable<MenuProps['items']>[number];
  };

  // Helper: find parent of a given target node within a route tree
  const findParent = (root: RouteItem, target: RouteItem): RouteItem | null => {
    if (!root.children) return null;
    for (const key in root.children) {
      const child = root.children[key];
      if (child === target) return root;
      const inSub = findParent(child, target);
      if (inSub) return inSub;
    }
    return null;
  };

  // Function to generate the context-aware menu items (AntD Menu format)
  const generateHeaderMenuItems = (): MenuProps['items'] => {
    // Always start with Home Page
    const headerMenuItems: MenuProps['items'] = [
      { key: '/home', icon: <HomeOutlined />, label: 'Home Page' }
    ];

    // 1. Handle Exclusions: No additional menu for home
    if (currentSectionKey === 'home') {
      return headerMenuItems;
    }

    // 2. Get the section route
    const sectionRoute = routes[currentSectionKey];
    if (!sectionRoute || !sectionRoute.children) {
      return headerMenuItems;
    }



    // 3) Keep header menu consistent on sub-pages:
    // Always render the top-level children of the current section (e.g. Machinery).
    // Dropdown arrows are driven by `grandchildren` (like Maintenance).
    for (const key in sectionRoute.children) {
      const child = sectionRoute.children[key];
      if (!child || !child.path || !(child.label || child.labelKey)) continue;
      if ((child.showInMenu && canReadPath(child.path)) || hasRenderableChildren(child)) {
        headerMenuItems.push(toMenuItem(child));
      }
    }

    // When inside /it/logs*, show only Dashboard, TKF Admin Logları and User Login Logs in header
    if (currentSectionKey === 'it') {
      if (currentPath.startsWith('/it/logs')) {
        const dashboardRoute = sectionRoute.children && (sectionRoute.children as any).dashboard;
        const logsRoute = sectionRoute.children && (sectionRoute.children as any).logs;
        const userLoginLogsRoute = logsRoute && logsRoute.children && (logsRoute.children as any).userLoginLogs;
        // Keep Home Page (index 0) and replace the rest
        (headerMenuItems as any[]).splice(1);
        if (dashboardRoute && dashboardRoute.showInMenu) {
          headerMenuItems.push({
            key: dashboardRoute.path,
            icon: dashboardRoute.icon as React.ReactNode,
            label: dashboardRoute.label as React.ReactNode,
          } as NonNullable<MenuProps['items']>[number]);
        }
        if (logsRoute && logsRoute.showInMenu) {
          headerMenuItems.push({
            key: logsRoute.path,
            icon: logsRoute.icon as React.ReactNode,
            label: logsRoute.label as React.ReactNode,
          } as NonNullable<MenuProps['items']>[number]);
        }
        if (userLoginLogsRoute && userLoginLogsRoute.showInMenu) {
          headerMenuItems.push({
            key: userLoginLogsRoute.path,
            icon: userLoginLogsRoute.icon as React.ReactNode,
            label: userLoginLogsRoute.label as React.ReactNode,
          } as NonNullable<MenuProps['items']>[number]);
        }
        return headerMenuItems;
      }
    }

    return headerMenuItems;
  };

  // Return the generated menu items and the section label for the title
  return {
    headerMenuItems: generateHeaderMenuItems(),
    currentSection: currentSectionLabel, // Use label for display
  };
};

// Generate landing (InitialMenu) items from route tree
// canRead verilirse read izni olmayan yollar menüden ayıklanır
export const getInitialMenuItems = (canRead?: (path: string) => boolean): MenuProps['items'] => {
  // Helpers local to this generator
  const isRenderable = (node?: RouteItem): boolean =>
    Boolean(
      node && (node.label || node.labelKey) && (node.showInMenu || node.children || node.grandchildren)
    );

  const visibleChildren = (node?: RouteItem): RouteItem[] =>
    Object.values(node?.children ?? {}).filter(
      (c): c is RouteItem =>
        Boolean(c && (c.label || c.labelKey) && (c.showInMenu || c.children || c.grandchildren))
    );

  const visibleGrandchildren = (node?: RouteItem): RouteItem[] =>
    Object.values(node?.grandchildren ?? {}).filter(
      (c): c is RouteItem => Boolean(c && (c.label || c.labelKey) && c.showInMenu)
    );

  const toMenuItem = (node: RouteItem): NonNullable<MenuProps['items']>[number] => {
    if (node.hideMenuChildren) {
      return {
        key: node.path,
        icon: node.icon as React.ReactNode,
        label: resolveRouteLabel(node),
      } as NonNullable<MenuProps['items']>[number];
    }

    // Prefer grandchildren if present; otherwise map direct children; else leaf
    const grands = visibleGrandchildren(node);
    if (grands.length > 0) {
      return {
        key: node.path,
        icon: node.icon as React.ReactNode,
        label: resolveRouteLabel(node),
        children: grands.map(g => ({
          key: g.path,
          icon: g.icon as React.ReactNode,
          label: resolveRouteLabel(g),
        })),
      } as NonNullable<MenuProps['items']>[number];
    }

    const childs = visibleChildren(node);
    if (childs.length > 0) {
      return {
        key: node.path,
        icon: node.icon as React.ReactNode,
        label: resolveRouteLabel(node),
        children: childs.map(c => ({
          key: c.path,
          icon: c.icon as React.ReactNode,
          label: resolveRouteLabel(c),
        })),
      } as NonNullable<MenuProps['items']>[number];
    }

    return {
      key: node.path,
      icon: node.icon as React.ReactNode,
      label: resolveRouteLabel(node),
    } as NonNullable<MenuProps['items']>[number];
  };

  const excludedTopLevels = new Set(['home', 'notFound']);

  const items: MenuProps['items'] = [];
  for (const [key, section] of Object.entries(routes)) {
    if (excludedTopLevels.has(key)) continue;
    if (!isRenderable(section)) continue;

    // Build top-level item with its visible children/grandchildren
    const children: NonNullable<MenuProps['items']>[number][] = [];
    if (section.children) {
      for (const childKey in section.children) {
        const child = section.children[childKey];
        if (isRenderable(child)) {
          children.push(toMenuItem(child));
        }
      }
    }

    if (children.length > 0) {
      items.push({
        key: section.path,
        icon: section.icon as React.ReactNode,
        label: resolveRouteLabel(section),
        children,
      } as NonNullable<MenuProps['items']>[number]);
    } else {
      items.push({
        key: section.path,
        icon: section.icon as React.ReactNode,
        label: resolveRouteLabel(section),
      } as NonNullable<MenuProps['items']>[number]);
    }
  }

  if (!canRead) {
    return items;
  }

  // read izni olmayan yaprakları ayıkla; çocuğu kalmayan ebeveyni de kaldır
  const prune = (menuItems: MenuProps['items']): MenuProps['items'] => {
    return (menuItems || [])
      .map(item => {
        if (!item) return null;
        const anyItem = item as any;
        if (Array.isArray(anyItem.children)) {
          const kids = prune(anyItem.children);
          if (kids && kids.length > 0) {
            return { ...anyItem, children: kids };
          }
          // Alt menüsü boşaldı: sadece kendi yolu okunabilirse yaprak olarak kalsın
          return canRead(String(anyItem.key)) ? { ...anyItem, children: undefined } : null;
        }
        return canRead(String(anyItem.key)) ? item : null;
      })
      .filter(Boolean) as MenuProps['items'];
  };

  return prune(items);
};
